import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { navigateWithPreference } from '../../../utils/navigationHelper';
import { apiFetch } from '../../../utils/api';
import PageHeader from '../../../components/PageHeader';

function SalesOrders() {
  const navigate = useNavigate();
  const [salesOrders, setSalesOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('All');
  
  const [visibleColumns, setVisibleColumns] = useState({
    order_number: true,
    customer_name: true,
    customer_order_number: true,
    order_date: true,
    status: true,
    item_count: true,
    total: true
  });

  useEffect(() => {
    fetchSalesOrders();
  }, []);

  const fetchSalesOrders = async () => {
    try {
      const res = await apiFetch('/api/sales-orders');
      const data = await res.json();
      setSalesOrders(data);
    } catch (err) {
      console.error('Error fetching sales orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleColumn = (column) => {
    setVisibleColumns(prev => ({ ...prev, [column]: !prev[column] }));
  };

  const statusTabs = ['All', 'Pending', 'Confirmed', 'Delivered', 'Invoiced'];
  
  const filtered = salesOrders.filter(order => {
    const matchesSearch = 
      order.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_order_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTab = activeTab === 'All' || order.status === activeTab;
    
    return matchesSearch && matchesTab;
  });

  const statusColors = {
    'Pending': 'bg-yellow-100 text-yellow-800',
    'Confirmed': 'bg-blue-100 text-blue-800',
    'Delivered': 'bg-green-100 text-green-800',
    'Invoiced': 'bg-gray-100 text-gray-800'
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading sales orders...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md sticky top-0 z-10" style={{ height: '116px' }}>
        <div className="max-w-full mx-auto px-6 h-full flex items-center justify-between">
          <button onClick={() => navigate('/crm')} className="text-white hover:text-blue-100 transition text-lg font-semibold">
            ⬅ Back
          </button>
          <h1 className="text-4xl font-bold flex-1 text-center">Sales Orders</h1>
          <button
            onClick={() => navigate('/crm/sales-orders/new')}
            className="px-4 py-2 bg-gray-300 text-black rounded-lg hover:bg-gray-400 transition font-medium text-sm shadow-sm"
          >
            + New Sales Order
          </button>
        </div>
      </div>

      {/* Search & Controls */}
      <div className="max-w-full px-4 py-4 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between gap-4">
          <input
            type="text"
            placeholder="Search sales orders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-indigo-500"
          />
          
          {/* Column Toggle */}
          <div className="relative group">
            <button className="px-3 py-2 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200">
              Columns ▾
            </button>
            <div className="hidden group-hover:block absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-10 p-2">
              {Object.keys(visibleColumns).map(col => (
                <label key={col} className="flex items-center px-3 py-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={visibleColumns[col]}
                    onChange={() => toggleColumn(col)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700 capitalize">{col.replace(/_/g, ' ')}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="max-w-full px-4 py-3 bg-white border-b border-gray-200">
        <div className="flex items-center gap-2">
          {statusTabs.map(tab => {
            const count = tab === 'All' 
              ? salesOrders.length 
              : salesOrders.filter(o => o.status === tab).length;
            
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  activeTab === tab
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300 hover:text-gray-900'
                }`}
              >
                {tab}
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab ? 'bg-gray-700' : 'bg-gray-200'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Table */}
      <div className="max-w-full px-4 py-4">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {visibleColumns.order_number && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order Number</th>}
                {visibleColumns.customer_name && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>}
                {visibleColumns.customer_order_number && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer PO</th>}
                {visibleColumns.order_date && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order Date</th>}
                {visibleColumns.status && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>}
                {visibleColumns.item_count && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>}
                {visibleColumns.total && <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center text-gray-500">
                    No sales orders found
                  </td>
                </tr>
              ) : (
                filtered.map((order, index) => (
                  <tr
                    key={order.id}
                    onClick={() => navigateWithPreference(navigate, `/crm/sales-orders/${order.id}`, 'sales-orders')}
                    className={`hover:bg-blue-200 cursor-pointer transition ${index % 2 === 1 ? 'bg-blue-100' : 'bg-white'}`}
                  >
                    {visibleColumns.order_number && (
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{order.order_number}</td>
                    )}
                    {visibleColumns.customer_name && (
                      <td className="px-4 py-3 text-sm text-gray-900">{order.customer_name || '—'}</td>
                    )}
                    {visibleColumns.customer_order_number && (
                      <td className="px-4 py-3 text-sm text-gray-600">{order.customer_order_number || '—'}</td>
                    )}
                    {visibleColumns.order_date && (
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {order.order_date ? new Date(order.order_date).toLocaleDateString() : '—'}
                      </td>
                    )}
                    {visibleColumns.status && (
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[order.status] || 'bg-gray-100 text-gray-800'}`}>
                          {order.status}
                        </span>
                      </td>
                    )}
                    {visibleColumns.item_count && (
                      <td className="px-4 py-3 text-sm text-gray-600 text-center">{order.item_count || 0}</td>
                    )}
                    {visibleColumns.total && (
                      <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                        £{parseFloat(order.total || 0).toFixed(2)}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
          
          {/* Footer */}
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-600">
            Showing {filtered.length} of {salesOrders.length} sales orders
          </div>
        </div>
      </div>
    </div>
  );
}

export default SalesOrders;
